import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatListModule } from "@angular/material/list";
import { MatIconModule } from "@angular/material/icon";
import { filter, map, of, switchMap } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ApiService } from "./core/api.service";
import { AuthService } from "./core/auth.service";

@Component({
  selector: "app-root",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatIconModule,
  ],
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent {
  appTitle = "Do Your Shades";

  readonly isMobile = signal(false);
  readonly sidebarOpen = signal(false);
  readonly newOrdersCount = signal(0);
  readonly authMode = signal(false);

  private readonly breakpoints = inject(BreakpointObserver);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly titleService = inject(Title);
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);

  private readonly customerNameCache = new Map<string, string>();
  private readonly roomNameCache = new Map<string, string>();

  constructor() {
    // Keep the session alive while the tab is open: refresh just before the
    // idToken expires and record activity for the 30-day idle timeout.
    for (const evt of ["pointerdown", "keydown", "scroll", "touchstart"]) {
      window.addEventListener(evt, () => this.auth.touch(), { passive: true });
    }
    window.setInterval(() => void this.auth.refreshIfNeeded(), 60_000);

    this.breakpoints
      .observe([Breakpoints.Handset])
      .pipe(takeUntilDestroyed())
      .subscribe((state) => {
        this.isMobile.set(state.matches);
        this.sidebarOpen.set(!state.matches);
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        // switchMap cancels any in-flight title API calls from a previous
        // navigation, so a slow response can never overwrite a newer title.
        switchMap((event: NavigationEnd) => {
          this.authMode.set(event.urlAfterRedirects.startsWith("/login"));
          if (this.authMode()) {
            return of("");
          }
          this.refreshNewOrders();
          return this.resolveTitle$();
        }),
        takeUntilDestroyed(),
      )
      .subscribe((title) => {
        this.titleService.setTitle(title);
        // A11y: move focus to the main landmark on each navigation.
        setTimeout(() => document.getElementById("main-content")?.focus(), 0);
      });

    if (!this.authMode()) {
      this.refreshNewOrders();
    }
  }

  async logout() {
    await this.auth.logout();
    this.router.navigate(["/login"]);
  }

  toggleSidebar() {
    this.sidebarOpen.update((open) => !open);
  }

  closeSidebar() {
    this.sidebarOpen.set(false);
  }

  private resolveTitle$() {
    let currentRoute = this.route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    const pageTitle = currentRoute.snapshot.data["title"] as string | undefined;
    const customerId =
      currentRoute.snapshot.paramMap.get("customerId") || "";
    const roomId = currentRoute.snapshot.paramMap.get("roomId") || "";

    if (roomId && customerId) {
      return this.resolveRoomTitle$(customerId, roomId, pageTitle);
    }
    if (customerId) {
      return this.resolveCustomerTitle$(customerId, pageTitle);
    }
    return of(pageTitle ? `${pageTitle} — Do Your Shades` : "Do Your Shades");
  }

  private resolveCustomerTitle$(
    customerId: string,
    pageTitle?: string,
  ) {
    const cached = this.customerNameCache.get(customerId);
    if (cached) {
      return of(this.formatCustomerTitle(cached, pageTitle));
    }
    return this.api.getCustomer(customerId).pipe(
      map((customer) => {
        const name = customer.name || "Customer";
        this.customerNameCache.set(customerId, name);
        return this.formatCustomerTitle(name, pageTitle);
      }),
    );
  }

  private resolveRoomTitle$(
    customerId: string,
    roomId: string,
    pageTitle?: string,
  ) {
    const cachedRoom = this.roomNameCache.get(roomId);
    const cachedCustomer = this.customerNameCache.get(customerId);
    if (cachedRoom && cachedCustomer) {
      return of(`${cachedRoom} — ${cachedCustomer} — Do Your Shades`);
    }

    return this.api.getRoom(customerId, roomId).pipe(
      switchMap((room) => {
        const roomName = room.name || "Room";
        this.roomNameCache.set(roomId, roomName);
        if (cachedCustomer) {
          return of(`${roomName} — ${cachedCustomer} — Do Your Shades`);
        }
        return this.api.getCustomer(customerId).pipe(
          map((customer) => {
            const customerName = customer.name || "Customer";
            this.customerNameCache.set(customerId, customerName);
            return `${roomName} — ${customerName} — Do Your Shades`;
          }),
        );
      }),
    );
  }

  private formatCustomerTitle(name: string, pageTitle?: string) {
    if (!pageTitle || pageTitle === "Customer") {
      return `${name} — Do Your Shades`;
    }
    return `${pageTitle} — ${name} — Do Your Shades`;
  }

  private refreshNewOrders() {
    if (!this.auth.isAuthenticated()) return;
    this.api.getOrders().subscribe({
      next: (orders) =>
        this.newOrdersCount.set(
          orders.filter((o) => o.status === "submitted").length,
        ),
      // 401/errors are handled by the auth interceptor; ignore here.
    });
  }
}
